import { useTranslate } from '@refinedev/core';
import { Card, Flex, Grid, Tabs, Tag, Typography, theme } from 'antd';
import type { BannerActionDetailOut } from '@/api/generated/schemas';
import { CoverThumb } from '@/components/common/show/CoverThumb';
import { StatCell } from '@/components/common/show/StatCell';
import { orDash } from '@/components/common/show/shared';
import { BANNER_ACTION_SHOW_TABS } from '../constants';
import type { BannerActionShowTab } from '../constants';

const { Text, Title } = Typography;

const TAB_LABEL_KEYS: Record<BannerActionShowTab, string> = {
  general: 'base.sections.basic_info',
};

interface BannerActionShowHeaderProps {
  record: BannerActionDetailOut;
  activeTab: BannerActionShowTab;
  onTabChange: (key: string) => void;
}

export const BannerActionShowHeader = ({
  record,
  activeTab,
  onTabChange,
}: BannerActionShowHeaderProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  return (
    <Card styles={{ body: { paddingBottom: 0 } }}>
      <Flex gap={token.marginLG} align="flex-start" vertical={!screens.sm}>
        <CoverThumb url={record.image_url} size={128} />

        <div style={{ flex: 1, minWidth: 0 }}>
          <Flex gap={token.marginSM} align="baseline" wrap>
            {/* Long names wrap instead of pushing the tabs bar out. */}
            <Title
              level={3}
              style={{
                margin: 0,
                fontSize: token.fontSizeHeading3,
                overflowWrap: 'anywhere',
              }}
            >
              {record.name}
            </Title>
            <Tag>{t(`user_catalog.banner_actions.size.${record.size}`)}</Tag>
            <Tag color="blue">
              {t(`user_catalog.banner_actions.kind.${record.kind}`)}
            </Tag>
          </Flex>

          <Text
            type="secondary"
            style={{ display: 'block', marginTop: token.marginXXS }}
          >
            {`ID ${record.id}`}
          </Text>

          <Flex
            gap={token.marginXL + token.margin}
            style={{ marginTop: token.marginLG }}
            wrap
          >
            <StatCell label={t('user_catalog.banner_actions.fields.target')}>
              {orDash(
                record.target_available != null && (
                  <Tag color={record.target_available ? 'green' : 'red'}>
                    {t(
                      `user_catalog.banner_actions.target_available.${
                        record.target_available ? 'yes' : 'no'
                      }`
                    )}
                  </Tag>
                )
              )}
            </StatCell>
            <StatCell label={t('user_catalog.banner_actions.fields.deeplink')}>
              {orDash(
                record.deeplink && (
                  <Text code style={{ fontSize: token.fontSize }}>
                    {record.deeplink}
                  </Text>
                )
              )}
            </StatCell>
          </Flex>
        </div>
      </Flex>

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        style={{ marginTop: token.marginSM }}
        tabBarStyle={{ marginBottom: 0 }}
        items={BANNER_ACTION_SHOW_TABS.map((key) => ({
          key,
          label: t(TAB_LABEL_KEYS[key]),
        }))}
      />
    </Card>
  );
};
